import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';
import { Theme } from '@ui/ThemeRegistry/theme.types';

export const defaultProps: ComponentsProps['Tabs'] = {
  orientation: 'vertical'
};

const styleOverrides: ComponentsOverrides<Theme>['Tabs'] = {
  root: ({ theme, ownerState }) => ({
    ...theme.mixins.applyBackgroundColor({ ownerState, theme }),
    containerType: 'inline-size',
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)'
  }),

  contentOuterGrid: {
    '> *': {
      gridColumnStart: 'auto'
    }
  },

  // tabsContext: {},
  tabListWrap: ({ ownerState, theme }) => ({
    'gridColumn': 'start/end',

    ...(ownerState?.orientation === 'vertical' && {
      gridColumnStart: 'full-start',
      gridColumnEnd: 'quarter',
      overflow: 'auto',
      maxHeight: '90vh',
      position: 'sticky',
      top: 0,
      alignSelf: 'start'
    }),

    '.MuiTabs-flexContainer': {
      [theme.containerBreakpoints.only('xs')]: {
        position: 'relative',
        minHeight: theme.spacing(10)
      },
      ...(ownerState?.orientation !== 'vertical' && {
        gap: 0
      })
    },

    '.MuiTabs-indicator': {
      ...(ownerState?.orientation !== 'vertical' && {
        display: 'none'
      })
    },

    '.MuiTab-root': {
      ...theme.typography.subtitle2,

      marginBottom: 0,
      padding: 'var(--grid-gap-sm) calc(var(--grid-gap-sm) / 2)',
      textTransform: 'initial',
      marginLeft: '-2px',

      ...(ownerState?.orientation === 'vertical'
        ? {
            '&': {
              textAlign: 'left',
              alignItems: 'flex-start',
              padding: 'var(--grid-gap-quarter) 0'
            }
          }
        : {
            flexGrow: 1,
            maxWidth: `${100 / (ownerState?.items?.length || 4)}%`,
            borderTopRightRadius: 'var(--grid-gap-md)',
            borderTopLeftRadius: 'var(--grid-gap-md)',
            backgroundColor: theme.palette.primary.main,
            marginTop: 'var(--grid-gap-half)',
            color: theme.palette.primary.contrastText,
            border: '2px solid',
            borderColor: theme.palette.primary.contrastText,

            [theme.containerBreakpoints.only('xs')]: {
              display: 'none'
            }
          })
    },

    '.Mui-selected': {
      backgroundColor: theme.palette.primary.contrastText,
      color: theme.palette.primary.main,

      [theme.containerBreakpoints.only('xs')]: {
        display: 'block',
        maxWidth: '100%',
        pointerEvents: 'none',
        padding: theme.spacing(0, 'var(--grid-margin-lg)')
      },

      ...(ownerState?.orientation !== 'vertical' && {
        padding: 'var(--grid-gap-sm) calc(var(--grid-gap-sm) / 2)',
        marginTop: 0
      })
    }
  }),

  arrowIconLeft: ({ ownerState, theme }) => ({
    display: 'none',
    [theme.containerBreakpoints.only('xs')]: {
      display: 'block',
      position: 'absolute',
      zIndex: 1,
      top: '50%',
      left: theme.spacing(4),
      transform: 'translate(-50%, -50%)',
      cursor: 'pointer'
    }
  }),

  arrowIconRight: ({ ownerState, theme }) => ({
    display: 'none',
    [theme.containerBreakpoints.only('xs')]: {
      display: 'block',
      position: 'absolute',
      zIndex: 1,
      top: '50%',
      right: theme.spacing(1),
      transform: 'translate(-50%, -50%)',
      cursor: 'pointer'
    }
  }),

  detailsWrap: ({ ownerState, theme }) => ({
    gridColumnStart: 'start',
    gridColumnEnd: 'end',
    padding: 'var(--grid-gap) 0',

    ...(ownerState?.orientation === 'vertical' && {
      gridColumnStart: 'quarter',
      gridColumnEnd: 'full-end',
      padding: 0,
      alignSelf: 'start',
      marginTop: 0
    })
  })

  // details: {}
};

const createVariants = (theme: Theme): ComponentsVariants['Tabs'] => [];

export const TabsTheme = (theme: Theme): ThemeOptions => ({
  components: {
    Tabs: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default TabsTheme;
